/**
 * Confirmação do "Excluir conta" (chamado 7116). Não barra quem tem assinatura
 * em uso: exige o ciente — com a data até quando o plano iria, vinda do
 * backend — e avisa que a exclusão é definitiva. O motivo é opcional.
 */
import { useEffect, useState } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';

import { ReasonFields } from '@/components/subscription/ReasonFields';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { AlertDialogCloseButton } from '@/components/ui/alert-dialog-close-button';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  ACCOUNT_DELETION_REASONS,
  DELETE_ACCOUNT_TITLE,
  DELETE_ACCOUNT_WARNING,
  deletionAcknowledgementText,
  deletionMembersText,
} from '@/lib/account-deletion';
import type {
  AccountDeletionReason,
  AccountDeletionSummary,
  DeleteAccountPayload,
} from '@/services/account.service';

interface DeleteAccountDialogProps {
  open: boolean;
  summary: AccountDeletionSummary;
  submitting: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (payload: DeleteAccountPayload) => void;
}

export function DeleteAccountDialog({
  open,
  summary,
  submitting,
  onOpenChange,
  onConfirm,
}: Readonly<DeleteAccountDialogProps>) {
  const [ciente, setCiente] = useState(false);
  const [motivo, setMotivo] = useState<AccountDeletionReason | ''>('');
  const [comentario, setComentario] = useState('');

  // Cada abertura recomeça do zero.
  useEffect(() => {
    if (open) {
      setCiente(false);
      setMotivo('');
      setComentario('');
    }
  }, [open]);

  const membros = deletionMembersText(summary.members_count);

  const confirmar = () => {
    if (!ciente) return;
    const texto = comentario.trim();
    onConfirm({
      acknowledged: true,
      ...(motivo ? { reason_category: motivo } : {}),
      ...(texto ? { reason: texto } : {}),
    });
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="sm:max-w-xl">
        <AlertDialogCloseButton onClick={() => onOpenChange(false)} disabled={submitting} />
        <AlertDialogHeader>
          <AlertDialogTitle className="px-6 sm:pl-0 sm:pr-8">{DELETE_ACCOUNT_TITLE}</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-2 text-sm">
              <p>{DELETE_ACCOUNT_WARNING}</p>
              {membros && <p className="font-medium text-foreground">{membros}</p>}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-3 text-left text-red-900 dark:border-red-800 dark:bg-red-900/20 dark:text-red-100">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <div className="flex items-start gap-2">
            <Checkbox
              id="deleteAccountAck"
              checked={ciente}
              onCheckedChange={(valor) => setCiente(valor === true)}
              className="mt-0.5 min-h-0 border-red-400 data-[state=checked]:bg-red-600 data-[state=checked]:border-red-600"
            />
            <Label htmlFor="deleteAccountAck" className="text-sm font-normal leading-snug cursor-pointer">
              {deletionAcknowledgementText(summary.situation ?? 'free', summary.access_until)}
            </Label>
          </div>
        </div>

        <ReasonFields<AccountDeletionReason>
          reasonLabel="Qual o principal motivo? (opcional)"
          options={ACCOUNT_DELETION_REASONS}
          reason={motivo}
          onReasonChange={setMotivo}
          comment={comentario}
          onCommentChange={setComentario}
        />

        <AlertDialogFooter className="gap-2 sm:gap-0">
          <Button variant="destructive" onClick={confirmar} disabled={submitting || !ciente}>
            {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Excluir minha conta
          </Button>
          <AlertDialogCancel
            className="bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground"
            disabled={submitting}
          >
            Manter minha conta
          </AlertDialogCancel>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default DeleteAccountDialog;
